import { EdisonWallBoxApiDto, InstallationStatementApiDto } from 'src/Libs/installation-statement/models/statement'
import { StatementFormData } from 'src/Libs/installation-statement/types/general'
import { AddressFormData } from 'src/Libs/installation-statement/types'
import { PhotovoltaicTrackerModel } from 'src/Libs/installation-statement/models/photovoltaic'
import { MeasurementsFormData, MeasurementsFormDataKeys } from 'src/Libs/installation-statement/models/measurements'
import { ImagesFormData } from 'src/Libs/installation-statement/models/images'
import { PhotovoltaicFormData } from 'src/Libs/installation-statement/configurations/photovoltaic'
import { StorageInstallationStatementModel } from 'src/Libs/installation-statement/models'
import { EdisonWallBoxFormData } from 'src/Libs/installation-statement/models/edisonWallBox'
import { DEFAULT_EDISON_WALLBOX_DATA } from 'src/Libs/installation-statement/configurations/edisonWallBox'
import { StorageInstallationStatementKeys } from 'src/Libs/installation-statement/models/storage'
import { CompanyLocationDto } from 'src/Libs/user/api/dto/user.dto'
import { Company } from '../models/company'

export const convertAddress = (
  data: InstallationStatementApiDto,
  companyAddress: CompanyLocationDto | null,
  companyInfo: Company | null
): AddressFormData => {
  // we check if any of the deviating address field contents deviate from the customer fields
  const hasDeviatingAddress =
    data.installationCountryCode !== data.customerCountryCode ||
    data.installationStreetName !== data.customerStreetName ||
    data.installationZip !== data.customerZip ||
    data.installationCity !== data.customerCity

  // and we also need to check whether all of them are empty
  const areAnyDeviatingAddressFieldsSet = !!(
    data.installationStreetName ||
    data.installationCity ||
    data.installationCountryCode ||
    data.installationZip
  )

  const deviatingAddressEnabled = areAnyDeviatingAddressFieldsSet && hasDeviatingAddress

  return {
    companyAddress: {
      addressAndHouseNumber: companyAddress?.PlaceOR || '',
      city: companyAddress?.Address || '',
      companyName: companyInfo?.Name || '',
      country: companyAddress?.Land || '',
      zipCode: companyAddress?.PLZ || '',
    },
    deviatingAddress: {
      addressAndHouseNumber: data.installationStreetName || '',
      city: data.installationCity || '',
      country: data.installationCountryCode || '',
      zipCode: data.installationZip || '',
    },
    customerAddress: {
      addressAndHouseNumber: data.customerStreetName || '',
      city: data.customerCity || '',
      country: data.customerCountryCode || '',
      firstName: data.customerName || '',
      lastName: data.customerSurname || '',
      zipCode: data.customerZip || '',
    },
    deviatingAddressEnabled,
    installer: {
      firstName: data.installerName || '',
      lastName: data.installerSurname || '',
    },
    /* we assume any partnerNumber from database is valid */
    partnerNumber: data.partnerNumber ? data.partnerNumber.toString() : '',
    partnerNumberEnabled: !!data.partnerNumber,
    partnerNumberValid: !!data.partnerNumber,
  }
}

const convertPhotovoltaicData = (data: InstallationStatementApiDto): PhotovoltaicFormData => {
  const trackers: PhotovoltaicTrackerModel[] = data.pvTrackers.map(pvTracker => {
    return {
      name: pvTracker.name,
      appliedToAllString: false,
      pvStrings: pvTracker.pvStrings.map(pvString => {
        return {
          pvModuleType: pvString.moduleType || '',
          moduleCount: pvString.pvOrientations[0].num,
          powerOptimizer: (pvString.powerOptimizer || '') as '' /* TODO: fix type assertions here */,
          direction: pvString.pvOrientations[0].direction as '',
          orientation: pvString.pvOrientations[0].orientation,
          testVoltage: pvString.testVoltage,
          modulePeakPower: pvString.modulePeakPower?.toFixed(2).toString() ?? null,
          openCircuitVoltage: pvString.openCircuitVoltage,
          positiveElectrodeGround: pvString.positiveElectrodeGround,
          negativeElectrodeGround: pvString.negativeElectrodeGround,
          riso: pvString.riso,
        }
      }),
    }
  })

  return {
    pvTrackers: trackers,
  }
}

const hasAnyEdisonData = (data: Record<string, never> | EdisonWallBoxApiDto): boolean => {
  if (!data) {
    return false
  }

  const dataFields = [
    'edisonConnectSn',
    'edisonPowerSn',
    'vehicleBatCapacity',
    'vehicleManufacturer',
    'vehicleModel',
    'vehicleSoftwareVersion',
    'edisonCb1',
    'edisonCb2',
    'edisonCb3',
    'edisonCb4',
    'edisonCb5',
    'edisonCb6',
    'edisonCb7',
    'edisonCb8',
  ]

  return dataFields.some(field => Boolean(data[field as keyof EdisonWallBoxApiDto]))
}

const convertStorageData = (data: InstallationStatementApiDto, isWallboxConnected: boolean): StorageInstallationStatementModel => {
  return {
    installationStatementId: data.storage[StorageInstallationStatementKeys.InstallationStatementId] ?? null,
    connectionType: data.storage[StorageInstallationStatementKeys.ConnectionType] ?? null,
    weatherConditions: data.storage[StorageInstallationStatementKeys.WeatherConditions] ?? null,
    netConfiguration: data.storage[StorageInstallationStatementKeys.NetConfiguration] ?? null,
    wallMounted: data.storage[StorageInstallationStatementKeys.WallMounted] ?? false,
    installationType: data.storage[StorageInstallationStatementKeys.InstallationType] ?? null,
    applicationArea: data.storage[StorageInstallationStatementKeys.ApplicationArea] ?? null,
    emergencyOption: data.storage[StorageInstallationStatementKeys.EmergencyOption] ?? false,
    distanceTop: data.storage[StorageInstallationStatementKeys.DistanceTop]?.toFixed(2).toString() ?? null,
    distanceFront: data.storage[StorageInstallationStatementKeys.DistanceFront]?.toFixed(2).toString() ?? null,
    distanceLeft: data.storage[StorageInstallationStatementKeys.DistanceLeft]?.toFixed(2).toString() ?? null,
    distanceRight: data.storage[StorageInstallationStatementKeys.DistanceRight]?.toFixed(2).toString() ?? null,
    temperature: data.storage[StorageInstallationStatementKeys.Temperature]?.toFixed(1).toString() ?? null,
    showEdisonWallboxStep: isWallboxConnected || hasAnyEdisonData(data.installationStatementBidi),
    hasBiDiWallbox: isWallboxConnected,
  }
}

const convertMeasurementsData = (data: InstallationStatementApiDto): MeasurementsFormData => {
  return {
    measurementDeviceManufacturer: data.storage[MeasurementsFormDataKeys.MeasurementDeviceManufacturer] ?? '',
    measurementDeviceType: data.storage[MeasurementsFormDataKeys.MeasurementDeviceType] ?? '',
    loopImpedanceNL1: data.storage[MeasurementsFormDataKeys.LoopImpedanceNL1]?.toFixed(2).toString() ?? null,
    loopImpedanceNL2: data.storage[MeasurementsFormDataKeys.LoopImpedanceNL2]?.toFixed(2).toString() ?? null,
    loopImpedanceNL3: data.storage[MeasurementsFormDataKeys.LoopImpedanceNL3]?.toFixed(2).toString() ?? null,
    loopImpedancePEL: data.storage[MeasurementsFormDataKeys.LoopImpedancePEL]?.toFixed(2).toString() ?? null,
  }
}

const convertImagesData = (data: InstallationStatementApiDto, imagesData: ImagesFormData): ImagesFormData => {
  const emptyImageData = {
    complete: { required: true },
    batteries: { required: true },
    connection: { required: true },
    'power-inverter': { required: false },
    'battery-box': { required: false },
  }

  return {
    deviceLinkId: data.deviceLinkId!,
    installationId: imagesData.installationId,
    ...(imagesData ? { images: imagesData.images } : { images: emptyImageData }),
  }
}

const convertEdisonData = (data: InstallationStatementApiDto): EdisonWallBoxFormData => {
  const { installationStatementBidi } = data

  if (!installationStatementBidi) {
    return DEFAULT_EDISON_WALLBOX_DATA
  }

  return {
    serialNumbers: {
      connect: installationStatementBidi.edisonConnectSn,
      power: installationStatementBidi.edisonPowerSn,
    },
    vehicleInformation: {
      manufacturer: installationStatementBidi.vehicleManufacturer,
      model: installationStatementBidi.vehicleModel,
      batteryCapacity: isNaN(Number(installationStatementBidi.vehicleBatCapacity)) ? null : Number(installationStatementBidi.vehicleBatCapacity),
      softwareVersion: installationStatementBidi.vehicleSoftwareVersion,
    },
    functionalityTests: {
      fullyInstalled: installationStatementBidi.edisonCb1,
      configured: installationStatementBidi.edisonCb2,
      scenarioOneTested: installationStatementBidi.edisonCb3,
      scenarioTwoTested: installationStatementBidi.edisonCb4,
      customerBriefed: installationStatementBidi.edisonCb5,
      biDirectionalDCChargingConfiguration: {
        positionedAtChargingPoint: installationStatementBidi.edisonCb6,
        chargingLocationDefined: installationStatementBidi.edisonCb7,
        functionActivated: installationStatementBidi.edisonCb8,
      },
    },
  }
}

export const fromStatementDtoToModelTransformer = ({
  data,
  imagesData,
  companyData,
  isWallboxConnected,
  companyInfo,
}: {
  data: InstallationStatementApiDto
  imagesData: ImagesFormData
  companyData: CompanyLocationDto | null
  isWallboxConnected: boolean
  companyInfo: Company | null
}): StatementFormData => {
  return {
    overviewData: { accepted: false },
    addressData: convertAddress(data, companyData, companyInfo),
    photovoltaicData: convertPhotovoltaicData(data),
    imagesData: convertImagesData(data, imagesData),
    storageData: convertStorageData(data, isWallboxConnected),
    measurementsData: convertMeasurementsData(data),
    edisonData: convertEdisonData(data),
  }
}
